import { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import Navbar from './navbar';
import Footer from './Footer';
import { FiSearch, FiBookmark, FiExternalLink, FiFilter, FiChevronDown, FiChevronUp } from 'react-icons/fi';
import { STARTUP_API_URL } from '../apiConfig';
import { motion } from 'framer-motion';
import { useProgressiveSearch } from '../hooks/useProgressiveSearch';
import RippleWaveLoader from './ui/RippleWaveLoader';

const STARTUP_PROMPTS = [
  'AI/ML startups in India',
  'Fintech Series A startups',
  'HealthTech early stage',
  'SaaS B2B cybersecurity',
  'CleanTech seed stage startups',
];

const LOADING_MESSAGES = [
  'Searching across startup databases…',
  'Enriching profiles with AI insights…',
  'Matching your criteria…',
  'Almost ready…',
];

interface Startup {
  name: string;
  description: string;
  website?: string;
  founded_year?: string;
  headquarters?: string;
  sector?: string;
  funding_stage?: string;
  total_funding?: string;
  employee_count?: string;
  technology_stack?: string[];
  key_investors?: string[];
  source?: string[];
}

const StartupDiscoveryPage = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [sector, setSector] = useState('');
  const [fundingStage, setFundingStage] = useState('');
  const [geography, setGeography] = useState('India');
  const [results, setResults] = useState<Startup[]>([]);
  const [loading, setLoading] = useState(false);
  const [hasSearched, setHasSearched] = useState(false);
  const [showFilters, setShowFilters] = useState(false);
  const [excludedCompanies, setExcludedCompanies] = useState<Record<string, boolean>>({});
  const navigate = useNavigate();
  const restoredRef = useRef(false);
  const webSearch = useProgressiveSearch();
  const [showWebSources, setShowWebSources] = useState(false);

  // Sectors list
  const sectors = [
    'AI/ML', 'Fintech', 'Healthcare', 'E-commerce', 'SaaS',
    'EdTech', 'CleanTech', 'Cybersecurity', 'Logistics', 'Manufacturing'
  ];

  // Funding stages
  const fundingStages = [
    'Pre-seed', 'Seed', 'Series A', 'Series B', 'Series C', 'Series D+', 'Growth'
  ];

  // Regions
  const regions = [
    'United States', 'Europe', 'India', 'Southeast Asia', 'LATAM', 'MENA', 'Global'
  ];

  // Restore from session storage
  useEffect(() => {
    if (restoredRef.current) return;
    restoredRef.current = true;

    const storedResults = sessionStorage.getItem('startupDiscoveryResults');
    const storedQuery = sessionStorage.getItem('startupDiscoveryQuery');
    const storedSector = sessionStorage.getItem('startupDiscoverySector');
    const storedStage = sessionStorage.getItem('startupDiscoveryStage');
    const storedGeo = sessionStorage.getItem('startupDiscoveryGeo');

    if (storedResults) {
      try {
        setResults(JSON.parse(storedResults));
        setHasSearched(true);
      } catch (_e) {
        console.error('Error parsing stored results');
      }
    }
    if (storedQuery) setSearchQuery(storedQuery);
    if (storedSector) setSector(storedSector);
    if (storedStage) setFundingStage(storedStage);
    if (storedGeo) setGeography(storedGeo);
  }, []);

  // Save to session storage
  useEffect(() => {
    sessionStorage.setItem('startupDiscoveryResults', JSON.stringify(results));
    sessionStorage.setItem('startupDiscoveryQuery', searchQuery);
    sessionStorage.setItem('startupDiscoverySector', sector);
    sessionStorage.setItem('startupDiscoveryStage', fundingStage);
    sessionStorage.setItem('startupDiscoveryGeo', geography);
  }, [results, searchQuery, sector, fundingStage, geography]);

  const handleSearch = async () => {
    if (!searchQuery.trim() && !sector && !fundingStage && !geography) return;

    setLoading(true);
    setHasSearched(true);
    setShowWebSources(true);

    // Progressive web search in parallel with AI endpoint
    const webParts = [searchQuery.trim(), sector, fundingStage, geography].filter(Boolean);
    const webQuery = webParts.length ? `${webParts.join(' ')} startup` : 'startups';
    webSearch.search(webQuery, geography === 'India' ? 'IN' : geography || 'IN', 20, 5);

    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/startup-discovery/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify({
          search_query: searchQuery,
          sector: sector || undefined,
          funding_stage: fundingStage || undefined,
          geography: geography || undefined,
          excluded_companies: excludedCompanies
        })
      });

      const data = await response.json();

      if (data.status === 'success') {
        setResults(data.data || []);
      } else {
        console.error('Search failed:', data.message);
      }
    } catch (error) {
      console.error('Error searching startups:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleSearch();
    }
  };

  const handleShowMore = async () => {
    // Add current results to excluded list and search again
    const newExcluded = { ...excludedCompanies };
    results.forEach(startup => {
      if (startup.name) {
        newExcluded[startup.name.toLowerCase()] = true;
      }
    });
    setExcludedCompanies(newExcluded);

    setLoading(true);
    try {
      const response = await fetch(`${STARTUP_API_URL}/api/v1/startup-discovery/search`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': localStorage.getItem('authToken') || ''
        },
        body: JSON.stringify({
          search_query: searchQuery,
          sector: sector || undefined,
          funding_stage: fundingStage || undefined,
          geography: geography || undefined,
          excluded_companies: newExcluded
        })
      });

      const data = await response.json();

      if (data.status === 'success' && data.data?.length > 0) {
        setResults([...results, ...data.data]);
      }
    } catch (error) {
      console.error('Error loading more:', error);
    } finally {
      setLoading(false);
    }
  };

  const clearFilters = () => {
    setSector('');
    setFundingStage('');
    setGeography('');
    setSearchQuery('');
    setExcludedCompanies({});
  };

  return (
    <div className="min-h-screen bg-black text-white">
      <Navbar />

      {/* Hero Section */}
      <section className="relative pt-32 pb-16 px-4 md:px-12 lg:px-24">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">
            Startup <span className="text-[#FF541F]">Discovery</span>
          </h1>
          <p className="text-gray-400 text-lg mb-8">
            Discover emerging startups across sectors, technologies, and funding stages
          </p>

          {/* Quick Prompts */}
          <div className="flex flex-wrap gap-2 justify-center mb-4 max-w-2xl mx-auto">
            {STARTUP_PROMPTS.map(p => (
              <button
                key={p}
                onClick={() => { setSearchQuery(p); }}
                className="px-3 py-1.5 text-xs rounded-full bg-white/5 border border-white/10 text-gray-400 hover:border-[#FF541F]/50 hover:text-[#FF541F] transition-all"
              >
                {p}
              </button>
            ))}
          </div>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mb-6">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="e.g., AI/ML startups in India at Seed stage…"
              className="w-full px-6 py-4 pr-14 rounded-xl bg-[#1A1A1A] border border-white/10 text-white placeholder-white/30 focus:outline-none focus:border-[#FF541F] transition-all text-[15px]"
            />
            <button
              onClick={handleSearch}
              disabled={loading}
              className="absolute right-2 top-1/2 -translate-y-1/2 p-3 bg-[#FF541F] rounded-lg hover:bg-[#FF541F]/80 transition-all disabled:opacity-50"
            >
              <FiSearch className="text-white" size={20} />
            </button>
          </div>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className="inline-flex items-center gap-2 px-4 py-2 text-sm text-gray-400 hover:text-white transition-colors"
          >
            <FiFilter size={16} />
            {showFilters ? 'Hide Filters' : 'Show Filters'}
          </button>

          {/* Filters */}
          {showFilters && (
            <motion.div
              initial={{ opacity: 0, height: 0 }}
              animate={{ opacity: 1, height: 'auto' }}
              className="mt-6 grid grid-cols-1 md:grid-cols-3 gap-4 max-w-3xl mx-auto"
            >
              <select
                value={sector}
                onChange={(e) => setSector(e.target.value)}
                className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
              >
                <option value="">All Sectors</option>
                {sectors.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <select
                value={fundingStage}
                onChange={(e) => setFundingStage(e.target.value)}
                className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
              >
                <option value="">All Stages</option>
                {fundingStages.map(s => (
                  <option key={s} value={s}>{s}</option>
                ))}
              </select>

              <select
                value={geography}
                onChange={(e) => setGeography(e.target.value)}
                className="px-4 py-3 rounded-lg bg-[#1A1A1A] border border-white/10 text-white focus:outline-none focus:border-[#FF541F]"
              >
                <option value="">All Regions</option>
                {regions.map(r => (
                  <option key={r} value={r}>{r}</option>
                ))}
              </select>

              <button
                onClick={clearFilters}
                className="md:col-span-3 text-sm text-[#FF541F] hover:underline"
              >
                Clear all filters
              </button>
            </motion.div>
          )}
        </div>
      </section>

      {/* Results Section */}
      <section className="px-4 md:px-12 lg:px-24 pb-16">
        {loading && (
          <div className="flex justify-center py-16">
            <RippleWaveLoader messages={LOADING_MESSAGES} />
          </div>
        )}

        {!loading && hasSearched && results.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400">No startups found. Try adjusting your search criteria.</p>
          </div>
        )}

        {!loading && results.length > 0 && (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {results.map((startup, index) => (
                <motion.div
                  key={`${startup.name}-${index}`}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-[#1A1A1A] rounded-xl p-6 border border-white/10 hover:border-[#FF541F]/50 transition-all hover:scale-[1.02] cursor-pointer"
                  onClick={() => navigate(`/startup/${encodeURIComponent(startup.name)}`)}
                >
                  <div className="flex justify-between items-start mb-4">
                    <h3 className="text-xl font-semibold text-white">{startup.name}</h3>
                    <div className="flex gap-2">
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          // Add to watchlist logic
                        }}
                        className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                      >
                        <FiBookmark size={18} className="text-gray-400 hover:text-[#FF541F]" />
                      </button>
                      {startup.website && (
                        <a
                          href={startup.website}
                          target="_blank"
                          rel="noopener noreferrer"
                          onClick={(e) => e.stopPropagation()}
                          className="p-2 hover:bg-white/10 rounded-lg transition-colors"
                        >
                          <FiExternalLink size={18} className="text-gray-400 hover:text-[#FF541F]" />
                        </a>
                      )}
                    </div>
                  </div>

                  <p className="text-gray-400 text-sm mb-4 line-clamp-2">
                    {startup.description || 'No description available'}
                  </p>

                  <div className="flex flex-wrap gap-2 mb-4">
                    {startup.sector && (
                      <span className="px-2 py-1 text-xs bg-[#FF541F]/20 text-[#FF541F] rounded">
                        {startup.sector}
                      </span>
                    )}
                    {startup.funding_stage && (
                      <span className="px-2 py-1 text-xs bg-blue-500/20 text-blue-400 rounded">
                        {startup.funding_stage}
                      </span>
                    )}
                    {startup.headquarters && (
                      <span className="px-2 py-1 text-xs bg-gray-500/20 text-gray-400 rounded">
                        {startup.headquarters}
                      </span>
                    )}
                  </div>

                  <div className="flex justify-between text-sm text-gray-500">
                    {startup.total_funding && (
                      <span>Raised: {startup.total_funding}</span>
                    )}
                    {startup.founded_year && (
                      <span>Founded: {startup.founded_year}</span>
                    )}
                  </div>

                  {startup.technology_stack && startup.technology_stack.length > 0 && (
                    <div className="mt-4 pt-4 border-t border-white/10">
                      <div className="flex flex-wrap gap-1">
                        {startup.technology_stack.slice(0, 3).map((tech, i) => (
                          <span key={i} className="px-2 py-1 text-xs bg-white/5 text-gray-400 rounded">
                            {tech}
                          </span>
                        ))}
                        {startup.technology_stack.length > 3 && (
                          <span className="px-2 py-1 text-xs text-gray-500">
                            +{startup.technology_stack.length - 3} more
                          </span>
                        )}
                      </div>
                    </div>
                  )}
                </motion.div>
              ))}
            </div>

            {/* Show More Button */}
            <div className="flex justify-center mt-8">
              <button
                onClick={handleShowMore}
                disabled={loading}
                className="px-6 py-3 bg-[#1A1A1A] border border-[#FF541F] text-[#FF541F] rounded-lg hover:bg-[#FF541F] hover:text-white transition-all disabled:opacity-50"
              >
                {loading ? 'Loading...' : 'Discover More Startups'}
              </button>
            </div>
          </>
        )}

        {/* Progressive Web Sources */}
        {showWebSources && (webSearch.results.length > 0 || webSearch.phase === 'serper' || webSearch.phase === 'enriched') && (
          <div className="mt-10">
            <button
              onClick={() => setShowWebSources(v => !v)}
              className="flex items-center gap-2 text-sm text-gray-400 hover:text-white mb-4 transition-colors"
            >
              {showWebSources ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
              Web Sources
              {webSearch.phase === 'serper' || webSearch.phase === 'enriched' ? (
                <span className="ml-2 flex items-center gap-1 text-xs text-[#FF541F]">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FF541F] animate-pulse" />
                  {webSearch.phase === 'serper' ? 'Loading more sources...' : 'Enriching results...'}
                </span>
              ) : (
                <span className="ml-2 text-xs text-gray-600">
                  {webSearch.results.length} results · {Object.entries(webSearch.sourceCount).map(([k, v]) => `${k}: ${v}`).join(', ')}
                </span>
              )}
            </button>

            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
              {webSearch.results.slice(0, 12).map((r, i) => (
                <a
                  key={i}
                  href={r.url}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="block p-4 bg-[#0F0F0F] border border-white/5 hover:border-white/20 rounded-lg transition-all group"
                >
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <p className="text-sm text-white font-medium line-clamp-2 group-hover:text-[#FF541F] transition-colors">
                      {r.title || r.url}
                    </p>
                    <FiExternalLink size={12} className="text-gray-600 flex-shrink-0 mt-0.5" />
                  </div>
                  {r.description && (
                    <p className="text-xs text-gray-500 line-clamp-2 mb-2">{r.description}</p>
                  )}
                  <div className="flex items-center gap-2">
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${
                      r.source === 'brave' ? 'bg-orange-500/20 text-orange-400' :
                      r.source === 'tavily' ? 'bg-blue-500/20 text-blue-400' :
                      'bg-purple-500/20 text-purple-400'
                    }`}>
                      {r.source || 'serper'}
                    </span>
                    {r.url && (
                      <span className="text-xs text-gray-600 truncate">
                        {(() => { try { return new URL(r.url!).hostname.replace('www.', ''); } catch { return r.url; } })()}
                      </span>
                    )}
                  </div>
                </a>
              ))}
            </div>
          </div>
        )}
      </section>

      <Footer />
    </div>
  );
};

export default StartupDiscoveryPage;
